import React from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Checkbox,
  ClickAwayListener,
  CircularProgress,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TablePagination,
  Tooltip,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PlaylistAddCheckOutlinedIcon from '@material-ui/icons/PlaylistAddCheckOutlined';
import SearchInput from 'components/SearchInput';
import { useDebounce } from 'helpers';
import Skeleton from 'react-loading-skeleton';

const useStyles = makeStyles(theme => ({
  popper: {
    zIndex: 9999
  },
  tooltipPopper: {
    zIndex: 10001
  },
  panel: {
    maxWidth: '90vw',
    maxHeight: '70vh',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  contentLayout: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden'
  },
  leftPanel: {
    width: '200px',
    minWidth: '180px',
    maxWidth: '200px',
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: '10px',
    marginRight: '10px',
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  rightPanel: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  filtersTitle: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: '6px'
  },
  filterAccordion: {
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
    marginBottom: '8px',
    '&:before': {
      display: 'none'
    },
    '&.Mui-expanded': {
      margin: '0 0 8px 0'
    }
  },
  filterAccordionSummary: {
    minHeight: '38px',
    '&.Mui-expanded': {
      minHeight: '38px'
    }
  },
  filterAccordionSummaryContent: {
    margin: '8px 0',
    '&.Mui-expanded': {
      margin: '8px 0'
    }
  },
  filterAccordionFlex: {
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
    marginBottom: '0',
    '&:before': {
      display: 'none'
    },
    '&.Mui-expanded': {
      margin: '0'
    }
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  filterHeaderTitle: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 600
  },
  clearAction: {
    fontSize: '0.7rem',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  filterAccordionDetails: {
    width: '100%',
    paddingTop: 0,
    paddingBottom: '8px',
    overflow: 'hidden',
    boxSizing: 'border-box'
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    flexShrink: 0
  },
  searchInputWrapper: {
    flex: 1
  },
  viewSelectedButton: {
    padding: '8px',
    color: 'rgba(0, 0, 0, 0.54)'
  },
  viewSelectedButtonActive: {
    padding: '8px',
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(25, 118, 210, 0.08)'
  },
  clearSelectionButton: {
    fontSize: '0.75rem',
    padding: '2px 8px',
    minWidth: 'auto',
    marginLeft: '8px',
    textTransform: 'none'
  },
  noResultsMessage: {
    padding: '16px',
    textAlign: 'center',
    color: 'rgba(0, 0, 0, 0.6)'
  },
  list: {
    flex: 1,
    minHeight: '300px',
    overflow: 'auto',
    border: 'none',
    borderRadius: 0
  },
  menuItem: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5)
  },
  optionContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  optionText: {
    flex: 1,
    minWidth: 0,
    maxWidth: '250px',
    overflow: 'hidden',
    '& .MuiTypography-root': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  },
  optionSecondary: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: '0.75rem',
    lineHeight: 1.3
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '8px',
    flexShrink: 0
  },
  paginationLoading: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: theme.palette.text.secondary,
    padding: '8px 12px'
  },
  statusOptions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    width: '100%'
  },
  statusChip: {
    height: '26px',
    borderRadius: '14px',
    '& .MuiChip-label': {
      fontSize: '0.75rem',
      fontWeight: 500,
      paddingLeft: '10px',
      paddingRight: '10px'
    }
  },
  tagOptions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    width: '100%',
    height: '220px',
    alignContent: 'flex-start',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: '4px',
    boxSizing: 'border-box'
  },
  tagChip: {
    maxWidth: '100%',
    height: '26px',
    borderRadius: '14px',
    '& .MuiChip-label': {
      display: 'block',
      fontSize: '0.75rem',
      fontWeight: 500,
      paddingLeft: '10px',
      paddingRight: '10px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '150px'
    }
  },
  emptyFilterText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic'
  },
  inputSkeleton: {
    minWidth: '100%'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0
  },
  button: {
    margin: '0px 8px',
    fontWeight: 'normal',
    color: 'rgba(0,0,0,.55) !important',
    opacity: '1 !important'
  },
  selected: {
    margin: '0px 8px',
    fontWeight: 'normal',
    color: theme.palette.primary.main + ' !important'
  }
}));

const emptyOptions = {
  list: [],
  keyId: 'uuid',
  valueId: 'uuid',
  displayValueId: 'name'
};

const MultiSelectDropdown = props => {
  const {
    name = 'multi-select',
    label,
    value = [],
    options = emptyOptions,
    onChange,
    onFocus,
    onBlur,
    disabled,
    dataTestId,
    remotePagination,
    searchValue: controlledSearchValue,
    pageNo: controlledPageNo,
    rowsPerPage: controlledRowsPerPage,
    totalCount,
    isLoading,
    // Status filter props
    statusOptions,
    selectedStatusValues,
    onStatusChange,
    // Tag filter props
    tagOptions,
    selectedTagValues,
    onTagChange,
    // Callbacks
    onOpen,
    onSearchChange,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPageOptions,
    showFieldSkeleton
  } = props;

  const classes = useStyles();
  const {
    list = [],
    keyId = 'uuid',
    valueId = 'uuid',
    displayValueId = 'name'
  } = options;
  const anchorRef = React.useRef(null);

  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [pageNo, setPageNo] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [remoteSearchValue, setRemoteSearchValue] = React.useState('');
  const [isRemoteSearchUserTriggered, setIsRemoteSearchUserTriggered] =
    React.useState(false);
  const [pendingSelections, setPendingSelections] = React.useState([]);
  const [isViewSelected, setIsViewSelected] = React.useState(false);
  const [isRowsPerPageMenuOpen, setIsRowsPerPageMenuOpen] = React.useState(false);

  const isRemote = !!remotePagination;
  const debouncedRemoteSearch = useDebounce(remoteSearchValue, 500);

  const computedPanelConfig = React.useMemo(() => {
    if (typeof window === 'undefined') {
      return {
        width: 550,
        placement: 'bottom-start'
      };
    }

    const viewportWidth = window.innerWidth || 550;
    const anchorRect = anchorRef.current?.getBoundingClientRect?.();

    if (!anchorRect) {
      return {
        width: Math.min(550, viewportWidth - 24),
        placement: 'bottom-start'
      };
    }

    const availableRight = Math.max(0, viewportWidth - anchorRect.left - 12);
    const availableLeft = Math.max(0, anchorRect.right - 12);
    const placement =
      availableRight >= availableLeft ? 'bottom-start' : 'bottom-end';
    const availableWidth =
      placement === 'bottom-start' ? availableRight : availableLeft;

    return {
      width: Math.min(550, viewportWidth - 24, availableWidth),
      placement
    };
  }, [open]);

  // Initialize pending selections when opening
  React.useEffect(() => {
    if (open) {
      setPendingSelections(Array.isArray(value) ? [...value] : []);
    }
  }, [open]);

  React.useEffect(() => {
    if (!isRemote || open) {
      return;
    }
    setRemoteSearchValue(controlledSearchValue || '');
  }, [isRemote, controlledSearchValue, open]);

  React.useEffect(() => {
    if (!isRemote || !open) {
      return;
    }
    if (!isRemoteSearchUserTriggered) {
      return;
    }
    if ((debouncedRemoteSearch || '') !== (remoteSearchValue || '')) {
      return;
    }
    const normalizedSearch = (debouncedRemoteSearch || '').trim();
    if (typeof onSearchChange === 'function') {
      onSearchChange(normalizedSearch);
    }
  }, [
    isRemote,
    debouncedRemoteSearch,
    remoteSearchValue,
    open,
    isRemoteSearchUserTriggered
  ]);

  const effectiveSearchValue =
    isRemote
      ? remoteSearchValue
      : searchValue;
  const effectivePageNo =
    isRemote
      ? (controlledPageNo ?? 0)
      : pageNo;
  const effectiveRowsPerPage =
    isRemote
      ? (controlledRowsPerPage ?? 10)
      : rowsPerPage;
  const effectiveTotalCount =
    isRemote
      ? (totalCount ?? 0)
      : list.length;
  const showPaginationLoading = isRemote && isLoading;

  const getOptionLabel = React.useCallback(
    option => {
      if (!option) {
        return '';
      }
      if (typeof option === 'string') {
        return option;
      }

      return (
        option?.[displayValueId] ||
        option?.name ||
        option?.label ||
        option?.templateName ||
        option?.title ||
        option?.[valueId] ||
        option?.[keyId] ||
        ''
      );
    },
    [displayValueId, valueId, keyId]
  );

  const getOptionValue = React.useCallback(
    option => {
      if (!option) {
        return undefined;
      }
      if (typeof option === 'string') {
        return option;
      }

      return (
        option?.[valueId] ||
        option?.[keyId] ||
        option?.uuid ||
        option?.id ||
        option?.templateUuid
      );
    },
    [valueId, keyId]
  );

  const filteredOptions = React.useMemo(() => {
    if (isRemote) {
      return list;
    }
    const searchLower = (searchValue || '').toLowerCase();
    return list.filter(option => {
      const optionName = (getOptionLabel(option) || '').toLowerCase();
      return optionName.includes(searchLower);
    });
  }, [list, searchValue, isRemote, getOptionLabel]);

  const paginatedOptions = React.useMemo(() => {
    if (isRemote) {
      return filteredOptions;
    }
    const startIndex = pageNo * rowsPerPage;
    return filteredOptions.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredOptions, pageNo, rowsPerPage, isRemote]);

  const isOptionSelected = optionValue => {
    return pendingSelections.some(sel => sel.value === optionValue);
  };

  const handleToggleOption = option => {
    const optionValue = getOptionValue(option);
    if (optionValue === undefined || optionValue === null) {
      return;
    }
    const isSelected = isOptionSelected(optionValue);
    const optionLabel = getOptionLabel(option) || optionValue;

    if (isSelected) {
      setPendingSelections(prev =>
        prev.filter(sel => sel.value !== optionValue)
      );
    } else {
      setPendingSelections(prev => [
        ...prev,
        {
          name,
          value: optionValue,
          label: optionLabel,
          checked: true
        }
      ]);
    }
  };

  const handleApply = () => {
    if (typeof onChange === 'function') {
      onChange(pendingSelections);
    }
    setOpen(false);
  };

  const handleClear = () => {
    setPendingSelections([]);
    // Clear all filters
    if (typeof onStatusChange === 'function') {
      onStatusChange([]);
    }
    if (typeof onTagChange === 'function') {
      onTagChange([]);
    }
  };

  const handleToggle = () => {
    if (!disabled) {
      setOpen(prev => {
        const nextOpen = !prev;
        if (nextOpen) {
          if (typeof onOpen === 'function') {
            onOpen('');
          }
          setIsViewSelected(false);
          if (isRemote) {
            setIsRemoteSearchUserTriggered(false);
            setRemoteSearchValue('');
          } else {
            setSearchValue('');
            setPageNo(0);
          }
        }
        return nextOpen;
      });
    }
  };

  const handleClose = event => {
    if (isRowsPerPageMenuOpen) {
      return;
    }

    if (
      event?.target?.closest?.('[data-multi-select-pagination-menu="true"]')
    ) {
      return;
    }

    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleSearchChange = event => {
    const nextValue = event.target.value || '';
    if (isRemote) {
      setIsRemoteSearchUserTriggered(true);
      setRemoteSearchValue(nextValue);
      return;
    }
    setSearchValue(nextValue);
  };

  const resetSearch = () => {
    if (isRemote) {
      setIsRemoteSearchUserTriggered(true);
      setRemoteSearchValue('');
      return;
    }

    setSearchValue('');
    setPageNo(0);
  };

  const handlePageChange = (_, page) => {
    if (isRemote) {
      if (typeof onPageChange === 'function') {
        onPageChange(page);
      }
      return;
    }

    setPageNo(page);
  };

  const handleRowsPerPageChange = event => {
    const nextRowsPerPage = parseInt(event.target.value, 10);
    if (isRemote) {
      if (typeof onRowsPerPageChange === 'function') {
        onRowsPerPageChange(nextRowsPerPage);
      }
      return;
    }

    setRowsPerPage(nextRowsPerPage);
    setPageNo(0);
  };

  // Status filter handlers
  const handleStatusToggle = statusValue => {
    const currentValues = Array.isArray(selectedStatusValues)
      ? selectedStatusValues
      : [];
    const isSelected = currentValues.includes(statusValue);
    const updatedValues = isSelected
      ? currentValues.filter(val => val !== statusValue)
      : [...currentValues, statusValue];

    // Reset pagination to page 0 when filter changes
    if (!isRemote) {
      setPageNo(0);
    }

    if (typeof onStatusChange === 'function') {
      // Pass resetPage flag so parent can reset pagination along with filter change
      onStatusChange(updatedValues, { resetPage: true });
    }
  };

  const clearStatusCategory = event => {
    event.stopPropagation();

    // Reset pagination to page 0 when filter is cleared
    if (!isRemote) {
      setPageNo(0);
    }

    if (typeof onStatusChange === 'function') {
      // Pass resetPage flag so parent can reset pagination along with filter change
      onStatusChange([], { resetPage: true });
    }
  };

  // Tag filter handlers
  const handleTagToggle = tagValue => {
    const currentValues = Array.isArray(selectedTagValues)
      ? selectedTagValues
      : [];
    const isSelected = currentValues.includes(tagValue);
    const updatedValues = isSelected
      ? currentValues.filter(val => val !== tagValue)
      : [...currentValues, tagValue];

    // Reset pagination to page 0 when filter changes
    if (!isRemote) {
      setPageNo(0);
    }

    if (typeof onTagChange === 'function') {
      // Pass resetPage flag so parent can reset pagination along with filter change
      onTagChange(updatedValues, { resetPage: true });
    }
  };

  const clearTagCategory = event => {
    event.stopPropagation();

    // Reset pagination to page 0 when filter is cleared
    if (!isRemote) {
      setPageNo(0);
    }

    if (typeof onTagChange === 'function') {
      // Pass resetPage flag so parent can reset pagination along with filter change
      onTagChange([], { resetPage: true });
    }
  };

  const handleButtonClick = event => {
    event.stopPropagation();
    handleToggle();
  };

  const isSelected = Array.isArray(value) && value.length > 0;

  // Generate tooltip text from selected values
  const selectedTooltip = isSelected 
    ? value.map(item => item.label || item.value).join(', ')
    : '';

  // Check if any filter is selected for Clear All button
  const hasAnyFilterSelected = 
    pendingSelections.length > 0 ||
    (Array.isArray(selectedStatusValues) && selectedStatusValues.length > 0) ||
    (Array.isArray(selectedTagValues) && selectedTagValues.length > 0);

  // Check if we should show the filters panel
  const hasFilters = 
    (Array.isArray(statusOptions) && statusOptions.length > 0) ||
    (Array.isArray(tagOptions) || typeof onTagChange === 'function');

  return (
    <>
      {showFieldSkeleton ? (
        <div className={classes.inputSkeleton}>
          <Skeleton height={40} />
        </div>
      ) : (
        <Tooltip title={selectedTooltip} disableHoverListener={!isSelected}>
          <Button
            ref={anchorRef}
            data-testid={dataTestId || `multi-select-dropdown-${name}`}
            className={isSelected ? classes.selected : classes.button}
            onClick={handleToggle}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={disabled}
            endIcon={<ExpandMoreIcon />}
          >
            {label}{isSelected ? ` (${value.length})` : ''}
          </Button>
        </Tooltip>
      )}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        modifiers={{
          flip: {
            enabled: true,
            behavior: ['bottom-end']
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport',
            priority: ['left', 'right'],
            padding: 8
          },
          computeStyles: {
            gpuAcceleration: false
          }
        }}
        transition
        className={classes.popper}
        style={{ zIndex: 9999 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                String(placement || '').startsWith('bottom') ? 'right top' : 'right bottom'
            }}
          >
            <Paper
              style={
                String(placement || '').startsWith('bottom')
                  ? { marginTop: 6 }
                  : { marginBottom: 6 }
              }
            >
              <ClickAwayListener onClickAway={handleClose}>
                <div
                  className={classes.panel}
                  style={{ width: `${computedPanelConfig.width}px` }}>
                  <div className={classes.contentLayout}>
                    {hasFilters && (
                      <div className={classes.leftPanel}>
                        <Typography className={classes.filtersTitle}>Filters</Typography>

                        {Array.isArray(statusOptions) && statusOptions.length > 0 && (
                          <Accordion className={classes.filterAccordion}>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon fontSize="small" />}
                              className={classes.filterAccordionSummary}
                              classes={{ content: classes.filterAccordionSummaryContent }}
                            >
                              <div className={classes.filterHeader}>
                                <Typography className={classes.filterHeaderTitle}>State</Typography>
                                <Typography
                                  className={classes.clearAction}
                                  onClick={clearStatusCategory}
                                >
                                  Clear
                                </Typography>
                              </div>
                            </AccordionSummary>
                            <AccordionDetails className={classes.filterAccordionDetails}>
                              <Box className={classes.statusOptions}>
                                {statusOptions.map(option => (
                                  <Chip
                                    key={option.value}
                                    className={classes.statusChip}
                                    clickable
                                    color={
                                      Array.isArray(selectedStatusValues) &&
                                      selectedStatusValues.includes(option.value)
                                        ? 'primary'
                                        : 'default'
                                    }
                                    variant={
                                      Array.isArray(selectedStatusValues) &&
                                      selectedStatusValues.includes(option.value)
                                        ? 'default'
                                        : 'outlined'
                                    }
                                    onClick={() => handleStatusToggle(option.value)}
                                    label={option.searchScope || option.label || option.value}
                                  />
                                ))}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        )}

                        {(Array.isArray(tagOptions) || typeof onTagChange === 'function') && (
                          <Accordion className={classes.filterAccordionFlex}>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon fontSize="small" />}
                              className={classes.filterAccordionSummary}
                              classes={{ content: classes.filterAccordionSummaryContent }}
                            >
                              <div className={classes.filterHeader}>
                                <Typography className={classes.filterHeaderTitle}>Tags</Typography>
                                <Typography
                                  className={classes.clearAction}
                                  onClick={clearTagCategory}
                                >
                                  Clear
                                </Typography>
                              </div>
                            </AccordionSummary>
                            <AccordionDetails className={classes.filterAccordionDetails}>
                              {Array.isArray(tagOptions) && tagOptions.length > 0 ? (
                                <Box className={classes.tagOptions}>
                                  {tagOptions.map(option => (
                                    <Tooltip
                                      key={option.value}
                                      title={option.label || option.value}
                                      arrow
                                    >
                                      <Chip
                                        className={classes.tagChip}
                                        clickable
                                        color={
                                          Array.isArray(selectedTagValues) &&
                                          selectedTagValues.includes(option.value)
                                            ? 'primary'
                                            : 'default'
                                        }
                                        variant={
                                          Array.isArray(selectedTagValues) &&
                                          selectedTagValues.includes(option.value)
                                            ? 'default'
                                            : 'outlined'
                                        }
                                        onClick={() => handleTagToggle(option.value)}
                                        label={option.label || option.value}
                                      />
                                    </Tooltip>
                                  ))}
                                </Box>
                              ) : (
                                <Typography className={classes.emptyFilterText}>
                                  No tags available for this context
                                </Typography>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </div>
                    )}

                    <div className={classes.rightPanel}>
                      <div className={classes.searchContainer}>
                        {!isViewSelected && (
                          <div className={classes.searchInputWrapper}>
                            <SearchInput
                              placeholder={label ? `Search ${label}` : 'Search'}
                              value={effectiveSearchValue}
                              onChange={handleSearchChange}
                              onClear={resetSearch}
                            />
                          </div>
                        )}
                        {isViewSelected && (
                          <div className={classes.searchInputWrapper}>
                            <Typography variant="body2" color="textSecondary">
                              Showing selected items ({pendingSelections.length})
                            </Typography>
                          </div>
                        )}
                        <Tooltip
                          title={isViewSelected ? 'Show All Options' : 'View Selected Options'}
                          PopperProps={{ className: classes.tooltipPopper }}
                        >
                          <IconButton
                            className={isViewSelected ? classes.viewSelectedButtonActive : classes.viewSelectedButton}
                            onClick={() => setIsViewSelected(prev => !prev)}
                            size="small"
                          >
                            <PlaylistAddCheckOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        {pendingSelections.length > 0 && (
                          <Tooltip title="Clear Selection">
                            <Button
                              className={classes.clearSelectionButton}
                              size="small"
                              color="primary"
                              onClick={() => setPendingSelections([])}
                            >
                              Clear Selection
                            </Button>
                          </Tooltip>
                        )}
                      </div>

                      <MenuList className={classes.list}>
                        {isViewSelected ? (
                          pendingSelections.length === 0 ? (
                            <Typography className={classes.noResultsMessage}>
                              No items selected
                            </Typography>
                          ) : (
                            pendingSelections.map(item => (
                              <MenuItem
                                key={item.value}
                                className={classes.menuItem}
                                onClick={() =>
                                  handleToggleOption({
                                    [valueId]: item.value,
                                    [displayValueId]: item.label
                                  })
                                }
                              >
                                <div className={classes.optionContainer}>
                                  <Checkbox
                                    checked={true}
                                    color="primary"
                                    size="small"
                                    style={{ padding: '4px', marginRight: '8px' }}
                                  />
                                  <Tooltip
                                    title={item.label || item.value}
                                    arrow
                                    placement="bottom-end"
                                    PopperProps={{ className: classes.tooltipPopper }}
                                  >
                                    <div className={classes.optionText}>
                                      <Typography variant="body2">{item.label || item.value}</Typography>
                                    </div>
                                  </Tooltip>
                                </div>
                              </MenuItem>
                            ))
                          )
                        ) : isLoading ? (
                          <MenuItem disabled>
                            <CircularProgress size={16} style={{ marginRight: 8 }} />
                            Loading options...
                          </MenuItem>
                        ) : paginatedOptions.length === 0 ? (
                          <MenuItem disabled>No options</MenuItem>
                        ) : (
                          paginatedOptions.map(option => (
                            <MenuItem
                              key={getOptionValue(option)}
                              className={classes.menuItem}
                              onClick={() => handleToggleOption(option)}
                            >
                              <div
                                className={classes.optionContainer}
                                style={{ alignItems: option?.secondaryText ? 'flex-start' : 'center' }}
                              >
                                <Checkbox
                                  checked={isOptionSelected(getOptionValue(option))}
                                  color="primary"
                                  size="small"
                                  style={{
                                    padding: '4px',
                                    marginRight: '8px',
                                    marginTop: option?.secondaryText ? '2px' : '0px'
                                  }}
                                />
                                <Tooltip
                                  title={getOptionLabel(option)}
                                  arrow
                                  placement="bottom-end"
                                  PopperProps={{ className: classes.tooltipPopper }}
                                >
                                  <div className={classes.optionText}>
                                    <Typography variant="body2">{getOptionLabel(option)}</Typography>
                                    {!!option?.secondaryText && (
                                      <Typography className={classes.optionSecondary}>
                                        {option.secondaryText}
                                      </Typography>
                                    )}
                                  </div>
                                </Tooltip>
                              </div>
                            </MenuItem>
                          ))
                        )}
                      </MenuList>

                      {!isViewSelected && (
                        <div className={classes.paginationContainer}>
                          {showPaginationLoading ? (
                            <div className={classes.paginationLoading}>
                              <CircularProgress size={14} />
                              <Typography variant="caption">Loading...</Typography>
                            </div>
                          ) : (
                            <TablePagination
                              component="div"
                              count={effectiveTotalCount}
                              page={effectivePageNo}
                              onPageChange={handlePageChange}
                              rowsPerPage={effectiveRowsPerPage}
                              onRowsPerPageChange={handleRowsPerPageChange}
                              SelectProps={{
                                onOpen: () => setIsRowsPerPageMenuOpen(true),
                                onClose: () => {
                                  setTimeout(() => {
                                    setIsRowsPerPageMenuOpen(false);
                                  }, 0);
                                },
                                MenuProps: {
                                  PaperProps: {
                                    'data-multi-select-pagination-menu': 'true'
                                  }
                                }
                              }}
                              rowsPerPageOptions={rowsPerPageOptions}
                              labelRowsPerPage=""
                              size="small"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={classes.buttonContainer}>
                    <Button
                      size="small"
                      onClick={handleClear}
                      disabled={!hasAnyFilterSelected}
                    >
                      Clear All
                    </Button>
                    <Button
                      size="small"
                      color="primary"
                      variant="contained"
                      onClick={handleApply}
                      disabled={
                        pendingSelections.length === 0 &&
                        (!Array.isArray(value) || value.length === 0)
                      }
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

MultiSelectDropdown.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.array,
  options: PropTypes.shape({
    list: PropTypes.array,
    keyId: PropTypes.string,
    valueId: PropTypes.string,
    displayValueId: PropTypes.string
  }),
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  dataTestId: PropTypes.string,
  remotePagination: PropTypes.bool,
  searchValue: PropTypes.string,
  pageNo: PropTypes.number,
  rowsPerPage: PropTypes.number,
  totalCount: PropTypes.number,
  isLoading: PropTypes.bool,
  // Status filter props
  statusOptions: PropTypes.array,
  selectedStatusValues: PropTypes.array,
  onStatusChange: PropTypes.func,
  // Tag filter props
  tagOptions: PropTypes.array,
  selectedTagValues: PropTypes.array,
  onTagChange: PropTypes.func,
  // Callbacks
  onOpen: PropTypes.func,
  onSearchChange: PropTypes.func,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  rowsPerPageOptions: PropTypes.array,
  showFieldSkeleton: PropTypes.bool
};

MultiSelectDropdown.defaultProps = {
  rowsPerPageOptions: [5, 10, 25]
};

export default MultiSelectDropdown;
